import { LitElement, html, css, nothing } from 'lit';
import { base, cards } from '../styles.js';

/**
 * The loan summary, read only. There are no inputs here and there must never be: cert 1.5/1.6
 * forbids editing loan data inside a partner iframe. Encompass owns the loan; we display it.
 *
 * Every field tolerates absence and renders a dash: the origin comes back with an empty loan
 * until ICE applies our data entitlements, and any one field can be blank on a loan early in its
 * life. Purpose and loan type arrive as ICE's own enum strings ($.property.loanPurposeType and
 * $.mortgageType, passed through by the BFF) and are labelled here; a value with no label is
 * shown as ICE sent it rather than hidden.
 */
export class LoanCard extends LitElement {
  /** missing: the card fields (loanNumber, borrowers, property, loanType, purpose) the intake would
   *  refuse the order for -- origin loanReadiness.fields (gap 1). Each is tagged so the officer sees where. */
  static properties = { loan: { type: Object }, missing: { type: Array } };

  static styles = [base, cards, css`
    :host { display: block; }
    .missing-tag { margin-left: 6px; padding: 1px 6px; border-radius: 999px; font-weight: 700;
      font-size: 10px; letter-spacing: .02em; text-transform: uppercase;
      color: #7a4a00; background: var(--pbg-warning-tint); }
  `];

  render() {
    const loan = this.loan ?? {};
    const borrowers = Array.isArray(loan.borrowers) ? loan.borrowers.filter(Boolean) : [];
    return html`
      <div class="card">
        <div class="card-head">
          <span class="ch-title">Loan &amp; borrower</span>
          <span class="ch-spacer"></span>
          <span class="ro-tag">Read-only</span>
        </div>
        <div class="card-body">
          <div class="summary-grid">
            ${this.#field('Loan number', 'loanNumber', text(loan.loanNumber), 'mono')}
            ${this.#field('Loan purpose', 'purpose', label(PURPOSE_LABELS, loan.purpose))}
            ${this.#field('Borrower(s)', 'borrowers', borrowers.length ? borrowers.join(' & ') : null)}
            ${this.#field('Loan type', 'loanType', label(LOAN_TYPE_LABELS, loan.loanType))}
            ${this.#field('Property address', 'property', text(loan.property), '', true)}
            ${this.#field('Loan amount', 'loanAmount', formatAmount(loan.loanAmount), 'big mono')}
            ${this.#field('Closing date', 'closingDate', formatDate(loan.closingDate), 'big')}
          </div>
        </div>
      </div>`;
  }

  #field(label, name, value, cls = '', span = false) {
    const empty = value === null || value === undefined || value === '';
    const missing = Array.isArray(this.missing) && this.missing.includes(name);
    return html`
      <div class="field ${span ? 'span' : ''}" data-missing=${missing ? name : nothing}>
        <span class="lbl">${label}${missing ? html` <span class="missing-tag">Incomplete</span>` : nothing}</span>
        <div class="val ${cls} ${empty ? 'empty' : ''}" data-field=${name}>${empty ? '—' : value}</div>
      </div>`;
  }
}

/** ICE's loanPurposeType values, keyed after normalising case, spaces, hyphens and underscores. */
const PURPOSE_LABELS = {
  purchase: 'Purchase',
  cashoutrefinance: 'Cash-out refinance',
  nocashoutrefinance: 'No cash-out refinance',
  constructiononly: 'Construction only',
  constructiontopermanent: 'Construction to permanent',
  other: 'Other',
};

/** ICE's mortgageType values. FarmersHomeAdministration is how the loan model spells USDA. */
const LOAN_TYPE_LABELS = {
  conventional: 'Conventional',
  fha: 'FHA',
  va: 'VA',
  farmershomeadministration: 'USDA',
  usda: 'USDA',
  heloc: 'HELOC',
  other: 'Other',
};

export function label(labels, value) {
  const s = text(value);
  if (s === null) return null;
  return labels[s.toLowerCase().replace(/[^a-z0-9]/g, '')] ?? s;
}

export { PURPOSE_LABELS, LOAN_TYPE_LABELS };

function text(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s || null;
}

export function formatAmount(amount) {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

export function formatDate(value) {
  if (!value) return null;
  const s = String(value);
  // A bare date (the BFF's DateOnly) is read as local midnight so it never slips a day.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

customElements.define('epc-loan-card', LoanCard);
